import branditRails from '../../../../api/brandit_rails'
import {
  findCheapestRateInGroup,
  createFreeShippingRate
} from '../../../../components/Checkout/ShippingMethodSelector/shippingGroupsHelpers'
import { recalculateTaxIfReady } from '../slice.checkoutTax'
import {
  clearCheckoutRatesErrorsAction,
  clearCheckoutRatesRatesAction,
  clearCheckoutRatesSelectedRateAction,
  setCheckoutRatesErrorsAction,
  setCheckoutRatesRatesAction,
  setCheckoutRatesStatusAction,
  setIsCalculatingAction,
  setCalculationIdAction,
  setShippingRateLogIdAction,
  setConfigurationIdAction,
  setCalculationProgressAction,
  setSelectedRateForGroupAction
} from './checkoutRates.actions'

/**
 * Create shipping group configuration (pre-configuration for async flow)
 * This is a background operation with no loading state
 * @param {Object} params - Configuration parameters
 * @param {Array} params.line_items - Array of line items with cart_item_id, quantity, and address info
 * @param {number} params.line_items[].cart_item_id - Cart item ID
 * @param {number} params.line_items[].quantity - Quantity to ship
 * @param {number} [params.line_items[].customer_address_id] - Address ID (logged-in users)
 * @param {Object} [params.line_items[].guest_address] - Full address object (guest users)
 * @returns {(dispatch: import('redux').Dispatch) => Promise<string|null>}
 */
export const setSelectedRateForGroupAndRecalculateTax = (groupId, rate) => async dispatch => {
  dispatch(setSelectedRateForGroupAction(groupId, rate))
  dispatch(recalculateTaxIfReady())
}

export const createShippingGroupConfiguration =
  (params = {}) =>
  async (dispatch, getState) => {
    try {
      const checkoutSessionToken = getState().checkoutSessionToken.token

      const { data } = await branditRails.post('/shipping_groups', {
        ...params,
        checkout_session_token: checkoutSessionToken
      })

      // Store configuration ID if returned
      if (data?.configuration_id) {
        dispatch(setConfigurationIdAction(data.configuration_id))
      }

      // Store grouping structure (how cart items are grouped into shipments)
      // This shows the user which items ship together before rates are calculated
      if (data?.grouping_structure) {
        dispatch(setCheckoutRatesRatesAction(data.grouping_structure))
      }

      return data?.configuration_id || null
    } catch {
      // Don't set errors since this is a background pre-configuration step
      // The actual rate calculation will handle errors
      return null
    }
  }

/**
 * Poll shipping rates with calculation ID
 * Recursively polls GET /shipping_rates until status is completed or failed
 * @param {string} calculationId - Calculation ID to poll for
 * @param {number} attempt - Current polling attempt (for max attempts check)
 * @returns {(dispatch: import('redux').Dispatch) => Promise<void>}
 */
export const pollShippingRates =
  (calculationId, attempt = 1) =>
  async dispatch => {
    const MAX_POLL_ATTEMPTS = 60 // 60 attempts * 2s = 2 minutes
    const POLL_INTERVAL = 2000 // 2 seconds

    try {
      const { data } = await branditRails.get('/shipping_rates', {
        params: { calculation_id: calculationId }
      })

      const { status, rates, error_message, completed_groups, total_groups, progress_percent } = data

      // Update progress if available
      if (completed_groups !== undefined && total_groups !== undefined) {
        dispatch(
          setCalculationProgressAction({
            completedGroups: completed_groups,
            totalGroups: total_groups,
            progressPercent: progress_percent
          })
        )
      }

      if (status === 'completed') {
        const shipmentGroups = rates?.shipment_groups || []
        dispatch(setCheckoutRatesRatesAction(shipmentGroups))

        // Auto-select cheapest rate for each group, or free shipping if no rates
        shipmentGroups.forEach(group => {
          if (group.rates?.length > 0) {
            const cheapestRate = findCheapestRateInGroup(group.rates)
            if (cheapestRate) {
              dispatch(
                setSelectedRateForGroupAndRecalculateTax(group.carrier_service_group_id, {
                  service_code: cheapestRate.service_code,
                  cost_in_cents: cheapestRate.cost_in_cents,
                  encrypted_cost: cheapestRate.encrypted_cost,
                  carrier_service_id: cheapestRate.carrier_service_id,
                  cart_item_ids: group.line_items.map(li => li.cart_item_id),
                  customer_address_id: group.destination_address?.id
                })
              )
            }
          } else {
            // Free shipping - no rates returned means all items ship free
            const freeRate = createFreeShippingRate(group.line_items, group.destination_address?.id)
            dispatch(setSelectedRateForGroupAndRecalculateTax(group.carrier_service_group_id, freeRate))
          }
        })

        dispatch(setIsCalculatingAction(false))
        dispatch(setCheckoutRatesStatusAction('succeeded'))
        dispatch(setCalculationProgressAction(null))
        return
      }

      if (status === 'failed') {
        // Log detailed error info for QA debugging
        console.error('[ShippingRates] Calculation failed:', {
          calculation_id: calculationId,
          error_message,
          completed_groups,
          total_groups,
          progress_percent
        })

        const userFriendlyMessage =
          error_message ||
          'Unable to calculate shipping rates for your address. Please verify your shipping address or contact support for assistance.'

        dispatch(
          setCheckoutRatesErrorsAction({
            action: 'pollShippingRates',
            message: userFriendlyMessage,
            technicalDetails: error_message // Preserve technical details for debugging if needed
          })
        )
        dispatch(setIsCalculatingAction(false))
        dispatch(setCheckoutRatesStatusAction('failed'))
        dispatch(setCalculationProgressAction(null))
        return
      }

      // Status is still pending, continue polling
      if (attempt >= MAX_POLL_ATTEMPTS) {
        dispatch(
          setCheckoutRatesErrorsAction({
            action: 'pollShippingRates',
            message: 'Shipping rate calculation timed out. Please try again.'
          })
        )
        dispatch(setIsCalculatingAction(false))
        dispatch(setCheckoutRatesStatusAction('failed'))
        dispatch(setCalculationProgressAction(null))
        return
      }

      // Schedule next poll
      setTimeout(() => {
        dispatch(pollShippingRates(calculationId, attempt + 1))
      }, POLL_INTERVAL)
    } catch (err) {
      dispatch(setIsCalculatingAction(false))
      dispatch(setCheckoutRatesStatusAction('failed'))
      dispatch(
        setCheckoutRatesErrorsAction({
          action: 'pollShippingRates',
          message: err.message || err.response?.data?.message || 'Failed to poll shipping rates'
        })
      )
    }
  }

/**
 * Fetch shipping rates asynchronously and start polling
 * @param {Object} params - Request parameters for the shipping rates API
 * @param {Array} params.line_items - Array of line items with cart_item_id, quantity, and address info
 * @param {number} params.line_items[].cart_item_id - Cart item ID
 * @param {number} params.line_items[].quantity - Quantity to ship
 * @param {number} [params.line_items[].customer_address_id] - Address ID (logged-in users)
 * @param {Object} [params.line_items[].guest_address] - Full address object (guest users)
 * @returns {(dispatch: import('redux').Dispatch) => Promise<void>}
 */
export const fetchShippingRates =
  (params = {}) =>
  async (dispatch, getState) => {
    try {
      const checkoutSessionToken = getState().checkoutSessionToken.token

      dispatch(clearCheckoutRatesRatesAction())
      dispatch(clearCheckoutRatesSelectedRateAction())

      dispatch(setIsCalculatingAction(true))
      dispatch(setCheckoutRatesStatusAction('loading'))
      dispatch(clearCheckoutRatesErrorsAction())

      const { data } = await branditRails.post('/shipping_rates', {
        ...params,
        checkout_session_token: checkoutSessionToken
      })

      // Store calculation ID and shipping rate log ID for tracking
      if (data?.calculation_id) {
        dispatch(setCalculationIdAction(data.calculation_id))
        dispatch(setShippingRateLogIdAction(data.shipping_rate_log_id))

        // Start polling immediately
        dispatch(pollShippingRates(data.calculation_id))
      } else {
        throw new Error('No calculation_id returned from POST /shipping_rates')
      }
    } catch (err) {
      dispatch(setIsCalculatingAction(false))
      dispatch(setCheckoutRatesStatusAction('failed'))
      dispatch(
        setCheckoutRatesErrorsAction({
          action: 'fetchShippingRates',
          message: err.message || err.response?.data?.message || 'Failed to start shipping rate calculation'
        })
      )
    }
  }
